import React from 'react';
import { Box, Chip, Collapse, Typography, useTheme } from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { TripEventDto } from '@acorn-portal/shared/src/services/telematicsDriverTripsApi';
import {
	getEventColor,
	getEventIcon,
	formatEventType,
	formatEventTime,
	formatEventLocation,
	formatEventDuration,
	getSeverityLabel,
} from './trip-event-utils';

/**
 * Props for the TripEventItem component.
 */
interface TripEventItemProps {
	/** Event data from the telematics API. */
	event: TripEventDto;
	/** Zero-based index of the event in the list (displayed as 1-based badge). */
	index: number;
	/** Whether the event details are expanded. */
	expanded: boolean;
	/** Callback fired when the item is clicked to toggle expansion. */
	onToggle: () => void;
	/** Whether this event is currently highlighted (e.g., selected on map). Default: false */
	highlighted?: boolean;
	/** Geocoded road name to display instead of coordinates. */
	roadName?: string;
}

/**
 * Expandable trip event item with severity-based styling.
 *
 * Displays a driving event (harsh braking, speeding, etc.) with a numbered
 * badge matching map markers, event type icon, location, and severity chip.
 * Clicking expands to show additional details like timestamp, duration, and
 * description. High severity events get special warning styling.
 *
 * @component
 * @example
 * ```tsx
 * <TripEventItem
 *   event={eventData}
 *   index={0}
 *   expanded={expandedId === eventData.eventId}
 *   onToggle={() => toggleExpanded(eventData.eventId)}
 *   highlighted={selectedEventId === eventData.eventId}
 *   roadName="A1 Motorway"
 * />
 * ```
 */
const TripEventItem: React.FC<TripEventItemProps> = ({
	event,
	index,
	expanded,
	onToggle,
	highlighted = false,
	roadName,
}) => {
	const theme = useTheme();
	const eventColor = getEventColor(event.severity, theme);
	const isHighSeverity = event.severity?.toUpperCase() === 'HIGH' || event.severity?.toUpperCase() === 'CRITICAL';

	// Get the event type icon component
	const EventIcon = getEventIcon(event.eventType);

	// Format the event type for display
	const eventTypeLabel = formatEventType(event.eventType);

	// Use road name if available, otherwise fall back to coordinates
	const locationText = roadName || formatEventLocation(event.location);

	return (
		<Box
			sx={{
				backgroundColor: highlighted ? theme.palette.action.selected : 'transparent',
				cursor: 'pointer',
				transition: 'background-color 0.2s ease',
				'&:hover': {
					backgroundColor: theme.palette.action.hover,
				},
			}}
			onClick={onToggle}
		>
			{/* Main Row - Always Visible */}
			<Box
				sx={{
					display: 'flex',
					alignItems: 'flex-start',
					py: 1.5,
					px: 2,
				}}
			>
				{/* Event Number Badge - matches map marker style */}
				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						flexShrink: 0,
						width: 28,
						height: 28,
						borderRadius: '50%',
						backgroundColor: eventColor,
						border: '2px solid #FFFFFF',
						boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
						mr: 1.5,
					}}
				>
					<Typography
						sx={{
							fontSize: '0.75rem',
							fontWeight: 'bold',
							color: '#FFFFFF',
							lineHeight: 1,
						}}
					>
						{index + 1}
					</Typography>
				</Box>

				{/* Event Info */}
				<Box sx={{ flex: 1, minWidth: 0 }}>
					{/* Event type with icon */}
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
						<EventIcon
							sx={{
								fontSize: 18,
								color: eventColor,
							}}
						/>
						<Typography
							variant='body2'
							sx={{
								fontWeight: 'bold',
								color: theme.palette.text.primary,
							}}
						>
							{eventTypeLabel}
						</Typography>
					</Box>

					{/* Location / Road Name */}
					<Typography
						variant='body2'
						sx={{
							color: theme.palette.text.secondary,
							fontSize: '0.8rem',
						}}
					>
						{locationText}
					</Typography>
				</Box>

				{/* Severity Badge */}
				<Chip
					label={getSeverityLabel(event.severity)}
					size='small'
					sx={{
						backgroundColor: eventColor,
						color: '#FFFFFF',
						fontWeight: 'bold',
						fontSize: '0.7rem',
						height: 24,
						flexShrink: 0,
					}}
				/>
			</Box>

			{/* Expanded Details */}
			<Collapse in={expanded}>
				<Box
					sx={{
						pl: 6.5,
						pr: 2,
						pb: 2,
					}}
				>
					{/* Time */}
					<Typography variant='body2' color='text.secondary' sx={{ mb: 0.5 }}>
						<strong>Time:</strong> {formatEventTime(event.timestampUtc)}
					</Typography>

					{/* Duration */}
					{event.durationMilliseconds !== undefined && (
						<Typography variant='body2' color='text.secondary' sx={{ mb: 0.5 }}>
							<strong>Duration:</strong> {formatEventDuration(event.durationMilliseconds)}
						</Typography>
					)}

					{/* Event Description from API */}
					{event.description && (
						<Box
							sx={{
								display: 'flex',
								alignItems: 'flex-start',
								gap: 1,
								backgroundColor: isHighSeverity
									? `${theme.palette.warning.main}1A` // 10% opacity
									: theme.palette.grey[100],
								border: isHighSeverity ? `1px solid ${theme.palette.warning.main}40` : 'none',
								borderRadius: 1,
								p: 1.5,
								mt: 1,
							}}
						>
							{isHighSeverity && (
								<WarningAmberIcon
									sx={{
										fontSize: 20,
										color: theme.palette.warning.main,
										flexShrink: 0,
										mt: 0.25,
									}}
								/>
							)}
							<Typography
								variant='body2'
								sx={{
									color: theme.palette.text.primary,
								}}
							>
								{event.description}
							</Typography>
						</Box>
					)}
				</Box>
			</Collapse>
		</Box>
	);
};

export default TripEventItem;
